import {
  publishBlockerSchema,
  type NotShownReason,
  type ProductStatus,
  type PublishBlocker,
} from '@avrahami/contracts';
import { formatDateTime, isApiError } from '@avrahami/ui';
import { he } from '../i18n/he';

/**
 * Why shoppers do not see a product the store holds: what it lacks, or READY when it lacks
 * nothing and only has to be switched on. Empty for a product that is shown, and for an archived
 * one (its status says it all).
 */
export function notShownReasons(product: {
  status: ProductStatus;
  publishBlockers: readonly PublishBlocker[];
}): NotShownReason[] {
  if (product.status !== 'DRAFT' && product.status !== 'HIDDEN') return [];
  const lacking = product.publishBlockers.filter((blocker) => blocker !== 'ARCHIVED');
  return lacking.length === 0 ? ['READY'] : lacking;
}

/**
 * A product update refused to show the product in the store (CONFLICT that names `status` with
 * the blockers): what it still lacks, said by name, since "a conflict" would leave the store
 * guessing. Null for any other answer.
 */
export function refusalOf(error: unknown): string | null {
  if (!isApiError(error, 'CONFLICT')) return null;
  const refused = error.details.flatMap((detail) => {
    const blocker = publishBlockerSchema.safeParse(detail.message);
    return detail.path === 'status' && blocker.success ? [blocker.data] : [];
  });
  if (refused.length === 0) return null;
  return `${he.products.cannotShow} ${refused.map((blocker) => he.publishBlockers[blocker]).join(', ')}.`;
}

/** A validity window (in the store's time zone) as one line ("start: … · end: …"); a dash when it never ends. */
export function windowText(
  item: { startsAt: string | null; endsAt: string | null },
  timeZone: string,
): string {
  if (item.startsAt === null && item.endsAt === null) return he.common.none;
  return [
    item.startsAt === null
      ? null
      : `${he.common.startsAt}: ${formatDateTime(item.startsAt, timeZone)}`,
    item.endsAt === null ? null : `${he.common.endsAt}: ${formatDateTime(item.endsAt, timeZone)}`,
  ]
    .filter((part) => part !== null)
    .join(' · ');
}
